'use client'

import { useEffect, useRef, useState } from 'react'
import { ButtonWithHoverReveal } from '../Common/ButtonWithHoverReveal'
import { useLanguage } from '../../providers/LanguageProvider'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'

export default function EnquiryForm() {
  const { language } = useLanguage()
  const isArabic = language === 'ar'
  const locale = isArabic ? 'ar' : 'en'
  const [selected, setSelected] = useState('')
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [services, setServices] = useState<
    { id: string; title: string | { en?: string; ar?: string } }[]
  >([])

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  // ✅ New state for messages
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [statusType, setStatusType] = useState<'success' | 'error' | null>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const getServiceTitle = (service: any) => {
    if (typeof service.title === 'string') return service.title
    return isArabic ? service.title?.ar : service.title?.en
  }

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_SERVER_URL}/api/services?locale=${locale}&sort=createdAt`,
        )
        const data = await res.json()
        if (Array.isArray(data?.docs)) {
          setServices(data.docs)
        }
      } catch (error) {
        console.error('Failed to fetch services:', error)
      }
    }

    fetchServices()
  }, [locale])

  const handleClick = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    const form = e.currentTarget
    const formData = new FormData(form)

    formData.append('service', selected)

    try {
      const res = await fetch(`/api/submit-get-a-quote`, {
        method: 'POST',
        body: formData,
      })

      if (!res.ok) throw new Error('Failed to send enquiry')

      setStatusType('success')
      setStatusMessage(isArabic ? 'تم إرسال النموذج بنجاح!' : 'Form submitted successfully!')
      form.reset()
      setSelected('')
    } catch (error) {
      console.error(error)
      setStatusType('error')
      setStatusMessage(isArabic ? 'فشل إرسال النموذج' : 'Failed to submit form')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ✅ Auto-hide message after 5s
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage(null)
        setStatusType(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [statusMessage])

  return (
    <section className="w-full py-18 lg:py-28 mt-16 lg:mt-0">
      <div className="section-container mx-auto">
        <form
          className={clsx('lg:space-y-10 space-y-8', isArabic ? 'text-right' : 'text-left')}
          dir={isArabic ? 'rtl' : 'ltr'}
          onSubmit={handleClick}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:gap-y-10 gap-y-8 gap-x-8 lg:gap-x-10">
            <input
              type="text"
              name="botCheck"
              autoComplete="new-password"
              className="hidden"
              tabIndex={-1}
            />
            <div>
              <label
                htmlFor="name"
                className="block text-[15px] md:text-[12.5px] font-normal text-gray-700 mb-1"
              >
                {isArabic ? 'الاسم الكامل' : 'Full Name'}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder={isArabic ? 'الاسم الكامل' : 'Full Name'}
                className="text-[15px] md:text-[12.5px] xl:text-[14px] w-full px-4 py-3 border-b-[0.5px] border-gray-500 bg-[#f5f5f5] placeholder-gray-500 text-gray-700 focus:outline-none"
                required
                minLength={2}
                maxLength={100}
                pattern="^[A-Za-z\s]{2,100}$"
                title="Only alphabets are allowed"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-[15px] md:text-[12px] font-medium text-gray-700 mb-1"
              >
                {isArabic ? 'البريد الإلكتروني' : 'E-Mail'}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder={isArabic ? 'عنوان البريد الإلكتروني' : 'E-Mail Address'}
                className="text-[15px] md:text-[12.5px] xl:text-[14px] w-full px-4 py-3 border-b-[0.5px] border-gray-500 bg-[#f5f5f5] placeholder-gray-500 text-gray-700 focus:outline-none"
                required
                maxLength={120}
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-[15px] md:text-[12px] font-medium text-gray-700 mb-1"
              >
                {isArabic ? 'رقم الاتصال' : 'Contact Number'}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                placeholder={isArabic ? 'رقم الاتصال' : 'Contact Number'}
                dir={isArabic ? 'rtl' : 'ltr'}
                className="text-[15px] md:text-[12.5px] xl:text-[14px] w-full px-4 py-3 border-b-[0.5px] border-gray-500 bg-[#f5f5f5] placeholder-gray-500 text-gray-700 focus:outline-none"
                required
                maxLength={15}
                pattern="^[0-9]{7,15}$"
                title="Enter a valid phone number (digits only, 7–15 digits)"
                onInput={(e) => {
                  const input = e.currentTarget
                  input.value = input.value.replace(/\D/g, '')
                }}
              />
            </div>

            <div>
              <label
                htmlFor="industry"
                className="block text-[15px] md:text-[12px] font-medium text-gray-700 mb-1"
              >
                {isArabic ? 'الصناعة' : 'Industry'}
              </label>
              <input
                type="text"
                id="industry"
                name="industry"
                placeholder={isArabic ? 'الصناعة' : 'Industry'}
                className="text-[15px] md:text-[12.5px] xl:text-[14px] w-full px-4 py-3 border-b-[0.5px] border-gray-500 bg-[#f5f5f5] placeholder-gray-500 text-gray-700 focus:outline-none"
                required
                minLength={2}
                maxLength={80}
              />
            </div>
          </div>

          {/* Bottom Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:gap-y-10 gap-y-8 gap-x-8 lg:gap-x-10">
            <div className="relative w-full" ref={dropdownRef}>
              <label
                htmlFor="service"
                className="block text-[15px] md:text-[12px] font-medium text-gray-700 mb-1"
              >
                {isArabic ? 'اختر الخدمة' : 'Select Service'}
              </label>

              <div className="relative">
                <button
                  type="button"
                  dir={isArabic ? 'rtl' : 'ltr'}
                  className={`text-[15px] md:text-[12.5px] xl:text-[14px] w-full px-4 py-3  border-b-[0.5px] border-gray-500 bg-[#f5f5f5] text-gray-700 appearance-none cursor-pointer ${
                    isArabic ? 'text-right' : 'text-left'
                  }`}
                  onClick={() => setOpen(!open)}
                >
                  {selected
                    ? getServiceTitle(services.find((s) => s.id === selected))
                    : isArabic
                      ? 'اختر الخدمة'
                      : 'Select Service'}
                </button>

                <ChevronDown
                  strokeWidth={1}
                  className={`absolute top-1/2 -translate-y-1/2 text-black pointer-events-none ${
                    isArabic ? 'left-6' : 'right-6'
                  }`}
                  size={21}
                />

                {open && (
                  <ul className="absolute z-10 w-full text-[14px] bg-[#f5f5f5] border border-gray-300 max-h-60 overflow-y-auto shadow-md">
                    {services.map((service, i) => (
                      <li
                        key={service.id}
                        className={`px-4 py-2 hover:bg-gray-200 text-[12px] md:text-[12.5px] ${services.length - 1 === i ? '' : 'border-b-[0.5px] border-gray-300'} xl:text-[14px] text-gray-700 cursor-pointer`}
                        onClick={() => {
                          setSelected(service.id)
                          setOpen(false)
                        }}
                      >
                        {getServiceTitle(service)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="col-span-1 md:col-span-2">
              <label
                htmlFor="message"
                className="block text-[15px] md:text-[12px] font-medium text-gray-700 mb-1"
              >
                {isArabic ? 'أرسل رسالة' : 'Send a Message'}
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                placeholder={isArabic ? 'اكتب رسالتك هنا' : 'Message'}
                className="text-[15px] md:text-[12.5px] xl:text-[14px] w-full h-[120px] px-4 py-3 border-b-[0.5px] border-gray-500 bg-[#f5f5f5] placeholder-gray-500 text-gray-700 resize-vertical focus:outline-none"
                required
                maxLength={1000}
              />
            </div>
          </div>
          {/* Submit Button (Aligned based on language) */}
          <button
            disabled={isSubmitting}
            type="submit"
            className={clsx(
              'opacity-100 disabled:opacity-50 disabled:cursor-not-allowed',
              isArabic ? 'text-right' : 'text-left',
            )}
          >
            <ButtonWithHoverReveal
              disabled={isSubmitting}
              text={
                isSubmitting
                  ? isArabic
                    ? 'جارٍ الإرسال...'
                    : 'Submitting...'
                  : isArabic
                    ? 'إرسال الاستفسار'
                    : 'Submit Enquiry'
              }
            />
          </button>

          {/* ✅ Inline message (auto hides after 5s) */}
          {statusMessage && (
            <p
              className={clsx(
                'mt-2 text-sm font-medium',
                statusType === 'success' ? 'text-green-600' : 'text-red-600',
                isArabic ? 'text-right' : 'text-left',
              )}
            >
              {statusMessage}
            </p>
          )}
        </form>
      </div>
    </section>
  )
}
